import { useEffect, useCallback } from 'react';
import { useWebSocketContextSafe } from '../../../features/real-time-updates/useWebSocketContext';
import { useQueryClient } from '@tanstack/react-query';
import type { Message } from '../types';

interface WebSocketMessagePayload {
    type: 'message_received' | 'message_updated' | 'message_deleted';
    data: Message;
}

export function useRealtimeMessages() {
    const queryClient = useQueryClient();
    const wsContext = useWebSocketContextSafe();

    const handleWebSocketMessage = useCallback((payload: WebSocketMessagePayload) => {
        const queryKey = ['messages'];

        switch (payload.type) {
            case 'message_received': {
                queryClient.setQueryData<Message[]>(queryKey, old => {
                    if (!old) return [payload.data];
                    if (old.some(m => m.id === payload.data.id)) return old;
                    return [payload.data, ...old];
                });
                break;
            }
            
            case 'message_updated': {
                queryClient.setQueryData<Message[]>(queryKey, old =>
                    old?.map(msg => msg.id === payload.data.id ? payload.data : msg) || []
                );
                break;
            }
            
            case 'message_deleted': {
                queryClient.setQueryData<Message[]>(queryKey, old =>
                    old?.filter(msg => msg.id !== payload.data.id) || []
                );
                break;
            }
        }
    }, [queryClient]);

    useEffect(() => {
        if (!wsContext?.lastMessage) return;

        const { lastMessage } = wsContext;
        
        if (lastMessage.type === 'message_received' || 
            lastMessage.type === 'message_updated' || 
            lastMessage.type === 'message_deleted') {
        handleWebSocketMessage(lastMessage as WebSocketMessagePayload);
        }
    }, [wsContext?.lastMessage, handleWebSocketMessage, wsContext]);

    return {
        isConnected: wsContext?.isConnected ?? false,
    };
}